import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  InformationCircleIcon,
  DocumentTextIcon,
  FolderIcon,
  BookOpenIcon,
  AcademicCapIcon,
  CheckCircleIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { HsePageChrome } from "../../../../components/dashboard/hse/HsePageChrome";

/**
 * HSE/KS Document Management Guide
 * Visual guide showing how the document management system works
 */
export const HseKsDocumentGuide = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);

  // Define the structure nodes for visualization
  const structureNodes = [
    {
      id: "company",
      type: "company",
      title: "Firma",
      description: "Ditt firma",
      x: 400,
      y: 50,
      width: 200,
      height: 80,
      color: "#3B82F6",
    },
    {
      id: "discipline",
      type: "discipline",
      title: "Fag/Bransje",
      description: "Prosjektering VVS, Ventilasjon UTF, etc.",
      x: 150,
      y: 200,
      width: 180,
      height: 100,
      color: "#10B981",
      parent: "company",
    },
    {
      id: "category",
      type: "category",
      title: "Kategori",
      description: "HMS, KS, MIL, KVALITET, LEDELSE, AKAN",
      x: 400,
      y: 200,
      width: 180,
      height: 100,
      color: "#F59E0B",
      parent: "discipline",
    },
    {
      id: "chapter",
      type: "chapter",
      title: "Kapittel",
      description: "1. Omfang, 2. Kontekst, 3. Ledelse, etc.",
      x: 650,
      y: 200,
      width: 180,
      height: 100,
      color: "#8B5CF6",
      parent: "category",
    },
    {
      id: "document",
      type: "document",
      title: "Dokument",
      description: "Policy, Prosedyre, Instruks, Skjema",
      x: 900,
      y: 200,
      width: 180,
      height: 100,
      color: "#EF4444",
      parent: "chapter",
    },
  ];

  // Define connections between nodes
  const connections = [
    { from: "company", to: "discipline", label: "Har mange" },
    { from: "discipline", to: "category", label: "Har kategorier" },
    { from: "category", to: "chapter", label: "Har kapitler" },
    { from: "chapter", to: "document", label: "Har dokumenter" },
  ];

  // Get node position
  const getNodePosition = (nodeId) => {
    const node = structureNodes.find((n) => n.id === nodeId);
    if (!node) return { x: 0, y: 0 };
    return {
      x: node.x + node.width / 2,
      y: node.y + node.height / 2,
    };
  };

  // Calculate connection path
  const calculatePath = (fromId, toId) => {
    const from = getNodePosition(fromId);
    const to = getNodePosition(toId);
    const midX = (from.x + to.x) / 2;
    return `M ${from.x} ${from.y} L ${midX} ${from.y} L ${midX} ${to.y} L ${to.x} ${to.y}`;
  };

  const handleNodeClick = (node) => {
    setSelectedNode(node.id === selectedNode?.id ? null : node);
  };

  const handleNodeHover = (node) => {
    setHoveredNode(node);
  };

  const handleNodeLeave = () => {
    setHoveredNode(null);
  };

  // Information sections
  const informationSections = [
    {
      id: "overview",
      title: "Oversikt",
      icon: InformationCircleIcon,
      content: (
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            Dokumentstyringssystemet er organisert i en hierarkisk struktur som gjør det enkelt å organisere og finne dokumenter.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">Struktur:</h4>
            <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
              <li><strong>Fag/Bransje:</strong> Velg først hvilket fag eller bransje dokumentet tilhører (f.eks. Prosjektering VVS, Ventilasjon UTF)</li>
              <li><strong>Kategori:</strong> Velg deretter kategori (HMS, KS, MIL, KVALITET, LEDELSE, AKAN)</li>
              <li><strong>Kapittel:</strong> Velg kapittel under kategorien</li>
              <li><strong>Dokument:</strong> Opprett eller rediger dokumentet</li>
            </ol>
          </div>
        </div>
      ),
    },
    {
      id: "disciplines",
      title: "Fag/Bransje",
      icon: AcademicCapIcon,
      content: (
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            Fag/Bransje representerer forskjellige fagområder eller bransjer som firmaet ditt arbeider med.
          </p>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-semibold text-green-900 mb-2">Eksempler:</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-green-800">
              <li>Prosjektering VVS</li>
              <li>Ventilasjon UTF</li>
              <li>Elektriker UTF</li>
              <li>Elektriker PRO</li>
              <li>Glassmester UTF</li>
            </ul>
          </div>
          <p className="text-sm text-gray-700">
            <strong>Viktig:</strong> Når du velger en fag/bransje i sidebar, vises kun kapitler og dokumenter som tilhører den valgte fag/bransjen.
          </p>
        </div>
      ),
    },
    {
      id: "categories",
      title: "Kategorier",
      icon: FolderIcon,
      content: (
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            Alle fag/bransjer deler de samme kategoriene. Dette gir en konsistent struktur på tvers av alle fagområder.
          </p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { code: "HMS", name: "HMS - Helse, Miljø og Sikkerhet", color: "bg-red-50 border-red-200 text-red-800" },
              { code: "KS", name: "KS - Kvalitetssikring", color: "bg-blue-50 border-blue-200 text-blue-800" },
              { code: "MIL", name: "MIL - Miljø", color: "bg-green-50 border-green-200 text-green-800" },
              { code: "KVALITET", name: "KVALITET - Kvalitetsstyring", color: "bg-purple-50 border-purple-200 text-purple-800" },
              { code: "LEDELSE", name: "LEDELSE - Ledelsessystem", color: "bg-yellow-50 border-yellow-200 text-yellow-800" },
              { code: "AKAN", name: "AKAN - Arbeidsmiljø og Kompetanse", color: "bg-indigo-50 border-indigo-200 text-indigo-800" },
            ].map((cat) => (
              <div key={cat.code} className={`border rounded-lg p-3 ${cat.color}`}>
                <div className="font-semibold text-sm">{cat.code}</div>
                <div className="text-xs mt-1">{cat.name}</div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      id: "chapters",
      title: "Kapitler",
      icon: BookOpenIcon,
      content: (
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            Kapitler organiserer dokumenter innenfor hver kategori. Hver fag/bransje kan ha sine egne kapitler under de samme kategoriene.
          </p>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h4 className="font-semibold text-purple-900 mb-2">Eksempel struktur:</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-purple-800">
              <li><strong>KVALITET kapittel 1-10:</strong> Felles kapitler for alle fag/bransjer</li>
              <li><strong>KVALITET kapittel 11-18:</strong> VVS Prosjektering spesifikke kapitler</li>
              <li><strong>HMS kapittel 11-17:</strong> Ventilasjon UTF spesifikke kapitler</li>
            </ul>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-semibold text-yellow-900 mb-2">Kapittel nummerering:</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-yellow-800">
              <li>Hovedkapitler: 1, 2, 3, etc.</li>
              <li>Underkapitler: 2.1, 2.2, 4.1, etc.</li>
              <li>Sub-underkapitler: 2.1.1, 2.1.2, etc.</li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: "documents",
      title: "Dokumenter",
      icon: DocumentTextIcon,
      content: (
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            Dokumenter er knyttet til både fag/bransje og kapittel. Når du velger en fag/bransje, vises kun dokumenter for den valgte fag/bransjen.
          </p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { type: "policy", name: "Policy", description: "Overordnede retningslinjer og prinsipper" },
              { type: "procedure", name: "Prosedyre", description: "Detaljerte beskrivelser av prosesser" },
              { type: "instruction", name: "Instruks", description: "Konkrete veiledninger for operasjoner" },
              { type: "form", name: "Skjema", description: "Standardiserte dokumenter for datainnsamling" },
              { type: "guidance", name: "Veiledning", description: "Rådgivende dokumenter" },
              { type: "record", name: "Registrering", description: "Dokumenterte oppføringer" },
            ].map((docType) => (
              <div key={docType.type} className="border border-gray-200 rounded-lg p-3 bg-white">
                <div className="font-semibold text-sm text-gray-900">{docType.name}</div>
                <div className="text-xs text-gray-600 mt-1">{docType.description}</div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      id: "workflow",
      title: "Arbeidsflyt",
      icon: CheckCircleIcon,
      content: (
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            Dokumenter går gjennom en godkjenningsprosess før de blir aktive.
          </p>
          <div className="space-y-3">
            {[
              { step: 1, status: "draft", name: "Kladd", description: "Dokumentet opprettes og redigeres" },
              { step: 2, status: "pending_approval", name: "Venter på godkjenning", description: "Dokumentet sendes til godkjenning" },
              { step: 3, status: "approved", name: "Godkjent", description: "Dokumentet er godkjent og aktivt" },
              { step: 4, status: "obsolete", name: "Foreldet", description: "Dokumentet er erstattet av ny versjon" },
            ].map((workflowStep) => (
              <div key={workflowStep.step} className="flex items-start space-x-3 border border-gray-200 rounded-lg p-3 bg-white">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-sm">
                  {workflowStep.step}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-sm text-gray-900">{workflowStep.name}</div>
                  <div className="text-xs text-gray-600 mt-1">{workflowStep.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      id: "creating",
      title: "Opprette dokumenter",
      icon: PlusIcon,
      content: (
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            Følg disse trinnene for å opprette et nytt dokument:
          </p>
          <ol className="list-decimal list-inside space-y-3 text-sm text-gray-700">
            <li>
              <strong>Velg fag/bransje:</strong> I sidebar, velg først hvilket fag/bransje dokumentet tilhører
            </li>
            <li>
              <strong>Velg kategori:</strong> Velg deretter kategori (HMS, KS, MIL, etc.)
            </li>
            <li>
              <strong>Velg kapittel:</strong> Velg kapittel under kategorien
            </li>
            <li>
              <strong>Klikk &quot;Nytt dokument&quot;:</strong> Klikk på &quot;Nytt dokument&quot; knappen i header
            </li>
            <li>
              <strong>Fyll ut skjema:</strong> Fyll ut dokumentinformasjonen:
              <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                <li>Tittel (påkrevd)</li>
                <li>Beskrivelse</li>
                <li>Fag/Bransje, Kategori og Kapittel (bruk den kombinert velgeren)</li>
                <li>Dokumenttype (Policy, Prosedyre, Instruks, etc.)</li>
                <li>Innhold (tekst eller fil)</li>
              </ul>
            </li>
            <li>
              <strong>Lagre:</strong> Klikk &quot;Opprett&quot; for å lagre dokumentet
            </li>
          </ol>
        </div>
      ),
    },
    {
      id: "chapters-guide",
      title: "Opprette kapitler",
      icon: BookOpenIcon,
      content: (
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            Kapitler organiseres automatisk når du laster inn fag/bransje templates fra innstillinger. Du kan også opprette egne kapitler:
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">Fra template:</h4>
            <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
              <li>Gå til Innstillinger → HSE/KS → Disciplines</li>
              <li>Velg hvilke fag/bransje templates du vil laste inn</li>
              <li>Klikk &quot;Last inn&quot; - dette oppretter automatisk kategorier og kapitler</li>
            </ol>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
            <h4 className="font-semibold text-green-900 mb-2">Manuelt:</h4>
            <ol className="list-decimal list-inside space-y-1 text-sm text-green-800">
              <li>Velg fag/bransje og kategori i sidebar</li>
              <li>Høyreklikk på en kategori eller kapittel</li>
              <li>Velg &quot;Legg til kapittel&quot;</li>
              <li>Fyll ut kapittelnummer og tittel</li>
              <li>Velg om det skal være et hovedkapittel eller underkapittel</li>
            </ol>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
            <h4 className="font-semibold text-yellow-900 mb-2">Kapittel nummerering tips:</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-yellow-800">
              <li>Bruk hele tall for hovedkapitler: 1, 2, 3, 4...</li>
              <li>Bruk desimaler for underkapitler: 2.1, 2.2, 4.1...</li>
              <li>Bruk flere desimaler for sub-underkapitler: 2.1.1, 2.1.2...</li>
              <li>Hver fag/bransje kan ha kapitler med samme nummer i forskjellige kategorier</li>
            </ul>
          </div>
        </div>
      ),
    },
  ];

  const [activeSection, setActiveSection] = useState("overview");

  return (
    <HsePageChrome
      title={t("Dokumentstyring Guide")}
      subtitle={t("Lær hvordan dokumentstyringssystemet fungerer")}
      backButton={{
        label: t("Tilbake til dokumenter"),
        action: () => navigate("/dashboard/hse-ks/documents"),
        icon: ArrowLeftIcon,
      }}
    >
      <div className="mt-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left sidebar - Information sections */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow rounded-lg p-4 sticky top-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t("Informasjon")}
              </h3>
              <nav className="space-y-1">
                {informationSections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeSection === section.id
                        ? "bg-blue-100 text-blue-900"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <section.icon className="h-5 w-5" />
                      <span>{section.title}</span>
                    </div>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main content - Visual map and active section */}
          <div className="lg:col-span-2 space-y-6">
            {/* Visual Structure Map */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t("Dokumentstruktur")}
              </h3>
              <div
                ref={canvasRef}
                className="border-2 border-gray-300 rounded-lg relative bg-gray-50 overflow-auto"
                style={{ width: "100%", height: "400px", minHeight: "400px" }}
              >
                <svg
                  width="100%"
                  height="100%"
                  style={{ position: "absolute", top: 0, left: 0, minWidth: "1200px" }}
                >
                  {/* Render connections */}
                  {connections.map((conn) => {
                    const path = calculatePath(conn.from, conn.to);
                    const fromPos = getNodePosition(conn.from);
                    const toPos = getNodePosition(conn.to);
                    const midX = (fromPos.x + toPos.x) / 2;
                    const midY = (fromPos.y + toPos.y) / 2;
                    
                    return (
                      <g key={`${conn.from}-${conn.to}`}>
                        <path
                          d={path}
                          stroke="#6B7280"
                          strokeWidth="2"
                          fill="none"
                          markerEnd="url(#arrowhead)"
                        />
                        <text
                          x={midX}
                          y={midY - 5}
                          textAnchor="middle"
                          className="text-xs fill-gray-600 font-medium"
                        >
                          {conn.label}
                        </text>
                      </g>
                    );
                  })}
                  <defs>
                    <marker
                      id="arrowhead"
                      markerWidth="10"
                      markerHeight="10"
                      refX="9"
                      refY="3"
                      orient="auto"
                    >
                      <polygon points="0 0, 10 3, 0 6" fill="#6B7280" />
                    </marker>
                  </defs>
                </svg>

                {/* Render nodes */}
                {structureNodes.map((node) => {
                  const isSelected = selectedNode === node.id;
                  const isHovered = hoveredNode?.id === node.id;
                  
                  return (
                    <div
                      key={node.id}
                      style={{
                        position: "absolute",
                        left: node.x,
                        top: node.y,
                        width: node.width,
                        height: node.height,
                        backgroundColor: node.color,
                        border: isSelected
                          ? "3px solid #1E40AF"
                          : isHovered
                          ? "2px solid #3B82F6"
                          : "2px solid #1E40AF",
                        borderRadius: "8px",
                        padding: "12px",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        transform: isHovered ? "scale(1.05)" : "scale(1)",
                        boxShadow: isSelected
                          ? "0 4px 6px rgba(0, 0, 0, 0.1)"
                          : "0 2px 4px rgba(0, 0, 0, 0.05)",
                      }}
                      onClick={() => handleNodeClick(node)}
                      onMouseEnter={() => handleNodeHover(node)}
                      onMouseLeave={handleNodeLeave}
                    >
                      <div className="text-white font-semibold text-sm mb-1">
                        {node.title}
                      </div>
                      <div className="text-white text-xs opacity-90">
                        {node.description}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Node details */}
              {selectedNode && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  {(() => {
                    const node = structureNodes.find((n) => n.id === selectedNode);
                    if (!node) return null;
                    
                    const details = {
                      company: {
                        title: "Firma",
                        description: "Ditt firma er toppnivået i strukturen. Alle fag/bransjer tilhører firmaet.",
                        tips: [
                          "Firmaet kan ha flere fag/bransjer",
                          "Hver fag/bransje kan ha sine egne kategorier og kapitler",
                        ],
                      },
                      discipline: {
                        title: "Fag/Bransje",
                        description: "Fag/Bransje representerer forskjellige fagområder eller bransjer. Når du velger en fag/bransje, vises kun kapitler og dokumenter for den valgte fag/bransjen.",
                        tips: [
                          "Eksempler: Prosjektering VVS, Ventilasjon UTF, Elektriker UTF",
                          "Hver fag/bransje deler de samme kategoriene (HMS, KS, etc.)",
                          "Men hver fag/bransje kan ha sine egne kapitler under kategoriene",
                        ],
                      },
                      category: {
                        title: "Kategori",
                        description: "Kategorier er delt mellom alle fag/bransjer. Dette gir en konsistent struktur.",
                        tips: [
                          "HMS - Helse, Miljø og Sikkerhet",
                          "KS - Kvalitetssikring",
                          "MIL - Miljø",
                          "KVALITET - Kvalitetsstyring",
                          "LEDELSE - Ledelsessystem",
                          "AKAN - Arbeidsmiljø og Kompetanse",
                        ],
                      },
                      chapter: {
                        title: "Kapittel",
                        description: "Kapitler organiserer dokumenter innenfor hver kategori. Hver fag/bransje kan ha sine egne kapitler.",
                        tips: [
                          "Kapitler kan være hovedkapitler (1, 2, 3) eller underkapitler (2.1, 2.2)",
                          "Hver fag/bransje kan ha kapitler med samme nummer i forskjellige kategorier",
                          "Kapitler opprettes automatisk når du laster inn fag/bransje templates",
                        ],
                      },
                      document: {
                        title: "Dokument",
                        description: "Dokumenter er knyttet til både fag/bransje og kapittel. Når du velger en fag/bransje, vises kun dokumenter for den valgte fag/bransjen.",
                        tips: [
                          "Dokumenttyper: Policy, Prosedyre, Instruks, Skjema, Veiledning, Registrering",
                          "Dokumenter går gjennom en godkjenningsprosess",
                          "Dokumenter har versjonskontroll",
                        ],
                      },
                    };
                    
                    const detail = details[node.type];
                    if (!detail) return null;
                    
                    return (
                      <div>
                        <h4 className="font-semibold text-blue-900 mb-2">{detail.title}</h4>
                        <p className="text-sm text-blue-800 mb-3">{detail.description}</p>
                        <ul className="list-disc list-inside space-y-1 text-sm text-blue-700">
                          {detail.tips.map((tip, index) => (
                            <li key={index}>{tip}</li>
                          ))}
                        </ul>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Active Information Section */}
            <div className="bg-white shadow rounded-lg p-6">
              {informationSections
                .filter((section) => section.id === activeSection)
                .map((section) => (
                  <div key={section.id}>
                    <div className="flex items-center space-x-3 mb-4">
                      <section.icon className="h-6 w-6 text-blue-600" />
                      <h3 className="text-xl font-semibold text-gray-900">
                        {section.title}
                      </h3>
                    </div>
                    <div className="prose prose-sm max-w-none">
                      {section.content}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </HsePageChrome>
  );
};

